//
//  Vatom Inc. Copyright (c) 2023, all rights reserved.
//
//  Licensed under the VatomInc SDK License (the "License"); you may not use this file or
//  the Wallet SDK except in compliance with the License accompanying it. Unless
//  required by applicable law or agreed to in writing, the VatomInc SDK distributed under
//  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
//  ANY KIND, either express or implied. See the License for the specific language
//  governing permissions and limitations under the License.
//

// import { BusinessSnapshot } from '@vatom/sdk/core'
// import { PageConfig } from '@vatom/sdk/react'
import jwtDecode from 'jwt-decode'
import { create } from 'zustand'

// export interface FeatureConfig {
//   scanner?: {
//     enabled?: boolean
//   }
//   business?: {
//     hideHeader?: boolean
//   }
//   inventory?: {
//     hideHeader?: boolean
//   }
//   vatom?: {
//     hideTokenActions?: boolean
//     disableNewTokenToast?: boolean
//     ignoreNewTokenToast?: [string]
//   }
//   hideNavigation?: boolean
// }

export type TabRoute = 'Home' | 'Wallet' | 'Map' | 'MapAr' | 'Connect'

export interface scanner {
  enabled: boolean
}

interface VatomConfig {
  visibleTabs?: string[]
  pageConfig?: PageConfig
  baseUrl?: string
  language?: string
  hideNavigation?: boolean
  hideTokenActions?: boolean
  hideDrawer?: boolean
  disableNewTokenToast?: boolean
  disableArPickup?: boolean
  scanner?: scanner
  mapStyle?: any
  emptyStateImage?: string
  emptyStateTitle?: string
  emptyStateMessage?: string
}

export const useVatomWalletStore = create<
  VatomConfig & {
    inventoryWasUpdated?: boolean
    setinventoryWasUpdated: (inventoryWasUpdated: boolean) => void

    // navigation is ready
    isNavigationReady?: boolean
    setNavigationReady: (navigationReady: boolean) => void

    navigationsOptions?: {
      route?: string
      params?: any
    }

    setRouteParams: (route: string, params: any) => void
  }
>(set => ({
  setinventoryWasUpdated: (inventoryWasUpdated: boolean) => {
    set({ inventoryWasUpdated })
    setTimeout(() => {
      set({ inventoryWasUpdated: !inventoryWasUpdated })
    }, 10000)
  },
  setNavigationReady: (isNavigationReady: boolean) => {
    set({ isNavigationReady })
  },

  setRouteParams: (route: string, params: any) => {
    set({ navigationsOptions: { route, params } })
  }
}))

type DecodedToken = {
  aud: string
  exp: number
  iat: number
  scope: string
}

export type WalletSdkMessages = {
  // TODO CHANGE THIS TO BE MORE SPECIFIC
  'walletsdk:getPublicProfile': {
    payload: { userId?: string }
    response: string
  }
  'walletsdk:isLoggedIn': { payload?: undefined; response: { loggedIn: boolean } }
  'walletsdk:getCurrentUser': { payload?: undefined; response: string }
  'walletsdk:performAction': {
    payload: {
      tokenId: string
      actionName: string // TODO: fix token.performAction to contain the union of possible actionNames
      actionPayload: any // TODO: fix token.performAction to contain the union of possible actions and their payloads
    }
    response: string
  }
  'walletsdk:combineToken': {
    payload: { thisTokenId: string; otherTokenId: string }
    response: string
  }
  'walletsdk:trashToken': { payload: { tokenId: string }; response: string }
  'walletsdk:getToken': { payload: { tokenId: string }; response: string }
  'walletsdk:getPublicToken': { payload: { tokenId: string }; response: string }
  'walletsdk:listTokens': { payload?: undefined; response: string }
  'wallet-sdk-init': {
    payload: {
      accessToken?: string
      businessId?: string
      config?: VatomConfig | undefined
      hostname?: string
      embeddedType?: string
      refreshToken?: string
    }
    response?: undefined
  }
  'vatomwallet:SendUsersToWallet': {
    payload?: undefined
    response: void
  }
  // 'vatomwallet:listActionHandlers': {
  //   payload: { vatom: string }
  //   response: {
  //     overrideDropAction: boolean
  //   }
  // }
  'vatomwallet:handleAction': {
    payload: { action: 'Drop'; vatomID: string } // | {action: "OtherAction", withDifferentPayload: string}
    response: void
  }
  'vatomwallet:loaded': {
    payload?: undefined
    response: void
  }
  'vatomwallet:navIsReady': {
    payload?: undefined
    response: void
  }
  'vatomwallet:getCurrentPosition': {
    payload?: undefined
    response: void
  }
  'locationManager:getCurrentPosition': {
    payload?: any
    response: boolean
  }
  'vatomwallet:pre-init': {
    payload?: undefined
    response: void
  }
  'walletsdk:getBusinessTabs': {
    payload?: undefined
    response: string
  }
  'walletsdk:navigateToTab': {
    payload: { route: TabRoute; params?: any }
    response: string
  }
  'walletsdk:navigate': {
    payload: { route: string; params?: any }
    response: string
  }
  'walletsdk:logOut': {
    payload: undefined
    response: string
  }
  'walletsdk:inventoryWasUpdated': {
    payload: undefined
    response: string
  }
  'walletsdk:openCommunity': {
    payload: {
      bussinesId: string
      communityId?: string
      roomId?: string | undefined
    }
    response: string
  }

  'walletsdk:navigationIsReady': {
    payload: undefined
    response: boolean
  }

  //...add more messages here
}

export type SDKEventHandler<TMessage extends keyof WalletSdkMessages> = (
  e: WalletSdkMessages[TMessage]['payload']
) => void

//
// This class handles interaction with a parent/child app, if this or that app has been embedded into another app via an iframe or web view.
// This class should be exactly the same on both apps.
//
// Usage for host app: var manager = new VatomWallet(iframeElement.contentWindow) (single instance)
// Usage for child app: VatomWallet.setup() (global instance)
export class VatomWallet {
  static _shared: VatomWallet

  /** Singleton instance */
  static get shared() {
    // Stop if not setup yet
    if (!this._shared) throw new Error('Please call VatomWallet.setup() first.')
    else return this._shared
  }

  /** Get if wallet is embedded or not */
  static isEmbedded() {
    let isEmbedded =
      (window.parent && window.parent !== window) ||
      sessionStorage?.getItem('isEmbedded') === 'true' ||
      sessionStorage?.getItem('embeddedType') !== null ||
      false
    isEmbedded = JSON.parse(isEmbedded.toString().toLowerCase())
    return isEmbedded
  }

  static getEmbeddedType() {
    const embeddedType = sessionStorage?.getItem('embeddedType') || ''
    return embeddedType
  }

  static async getVatomAccessToken() {
    try {
      const vatomAccessToken = sessionStorage?.getItem('VATOM_ACCESS_TOKEN') || false
      const vatomRefreshToken = sessionStorage?.getItem('VATOM_REFRESH_TOKEN') || false
      if (!vatomAccessToken) {
        return false
      }

      const decodedToken = jwtDecode<DecodedToken>(vatomAccessToken)

      if (decodedToken.aud === 'wallet-rn') {
        return {
          accessToken: vatomAccessToken,
          refreshToken: vatomRefreshToken,
          expiresIn: decodedToken.exp - Math.round(Date.now() / 1000),
          issuedAt: decodedToken.iat,
          tokenType: 'Bearer',
          scope: decodedToken.scope
        }
      }
      const urlencoded = new URLSearchParams({
        grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
        client_id: decodedToken.aud,
        resource: 'wallet-rn',
        subject_token_type: 'urn:ietf:params:oauth:token-type:access_token',
        subject_token: vatomAccessToken
      })

      const res = await fetch('https://id.vatom.com/token', {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: urlencoded
      })

      const data = await res.json()
      const exp = data.expires_in || data.exp || decodedToken.exp

      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: exp,
        issuedAt: decodedToken.iat,
        tokenType: 'Bearer',
        scope: decodedToken.scope
      }
    } catch (error) {
      console.error('getVatomAccessToken', error)
      return false
    }
  }

  static getBusinessId() {
    try {
      const businessId = sessionStorage?.getItem('VATOM_BUSINESS_ID') || false

      return businessId
    } catch (error) {
      return false
    }
  }

  static SDKDrawerHidden() {
    try {
      if (!VatomWallet.isEmbedded()) {
        return false
      }
      const config = sessionStorage?.getItem('VATOM_CONFIG') || false

      if (!config) {
        return true
      }

      const vatomConfig = JSON.parse(config) as VatomConfig

      if (vatomConfig?.hideDrawer === null || vatomConfig?.hideDrawer === undefined) {
        return true
      }

      return vatomConfig?.hideDrawer
    } catch (error) {
      return true
    }
  }

  static SDKMaxDistanceForPickup() {
    try {
      if (!VatomWallet.isEmbedded()) {
        return 0
      }
      const config = sessionStorage?.getItem('VATOM_CONFIG') || false

      if (!config) {
        return 0
      }

      const vatomConfig = JSON.parse(config) as VatomConfig

      if (
        vatomConfig?.pageConfig?.features?.maxDistanceForPickup === null ||
        vatomConfig?.pageConfig?.features?.maxDistanceForPickup === undefined
      ) {
        return 0
      }

      return vatomConfig?.pageConfig?.features?.maxDistanceForPickup
    } catch (error) {
      return 0
    }
  }

  static SDKdisableArPickup() {
    try {
      if (!VatomWallet.isEmbedded()) {
        return false
      }
      const config = sessionStorage?.getItem('VATOM_CONFIG') || false

      if (!config) {
        return true
      }

      const vatomConfig = JSON.parse(config) as VatomConfig

      if (vatomConfig?.disableArPickup === null || vatomConfig?.disableArPickup === undefined) {
        return true
      }

      return vatomConfig?.disableArPickup
    } catch (error) {
      return true
    }
  }

  static SDKNavigationHidden() {
    try {
      if (!VatomWallet.isEmbedded()) {
        return false
      }
      const config = sessionStorage?.getItem('VATOM_CONFIG') || false

      if (!config) {
        return true
      }

      const vatomConfig = JSON.parse(config) as VatomConfig

      if (vatomConfig?.hideNavigation === null || vatomConfig?.hideNavigation === undefined) {
        return true
      }

      return vatomConfig?.hideNavigation
    } catch (error) {
      return true
    }
  }

  static getTabConfigs(): string[] {
    try {
      const config = sessionStorage?.getItem('VATOM_CONFIG') || false

      if (!config) {
        return []
      }

      const vatomConfig = JSON.parse(config) as VatomConfig

      return vatomConfig?.visibleTabs ?? []
    } catch (error) {
      return []
    }
  }
  static getMapStyles(): any {
    try {
      const config = sessionStorage?.getItem('VATOM_CONFIG') || false

      if (!config) {
        return null
      }

      const vatomConfig = JSON.parse(config) as VatomConfig

      return vatomConfig?.mapStyle ?? null
    } catch (error) {
      return null
    }
  }

  static getLanguage() {
    try {
      const config = sessionStorage?.getItem('VATOM_CONFIG') || false

      if (!config) {
        return 'en'
      }

      const vatomConfig = JSON.parse(config) as VatomConfig

      return vatomConfig?.language || 'en'
    } catch (error) {
      return 'en'
    }
  }

  static tokenActionsDisabled() {
    try {
      const config = sessionStorage?.getItem('VATOM_CONFIG') || false

      if (!config) {
        return false
      }

      const vatomConfig = JSON.parse(config) as VatomConfig

      return !!vatomConfig?.hideTokenActions
    } catch (error) {
      return false
    }
  }

  static getScannerConfig() {
    try {
      const config = sessionStorage?.getItem('VATOM_CONFIG') || false

      if (!config) {
        return false
      }

      const vatomConfig = JSON.parse(config) as VatomConfig
      return vatomConfig?.scanner || { enabled: true }
    } catch (error) {
      return false
    }
  }
  static getSDKPageConfig() {
    try {
      const config = sessionStorage?.getItem('VATOM_CONFIG') || false

      if (!config) {
        return false
      }

      const vatomConfig = JSON.parse(config) as VatomConfig

      return vatomConfig?.pageConfig ?? false
    } catch (error) {
      return false
    }
  }

  /** Setup the shared instance */
  static setup(elem?: HTMLDivElement) {
    // Stop if setup already
    if (this._shared) return

    // Set it up
    this._shared = new VatomWallet(elem)
  }

  static isRedirectedToBusiness() {
    return this._shared.redirectedToBusiness
  }

  static setRedirectedToBusiness(redirected: boolean) {
    this._shared.redirectedToBusiness = redirected
  }

  /** @private Pending promises */
  pending: {
    [key: string]: {
      resolve: (value: WalletSdkMessages[keyof WalletSdkMessages]['payload']) => void
      reject: (reason?: string | Error) => void
      timer: NodeJS.Timeout
    }
  } = {}

  /** Register handlers */
  handlers: { [key in keyof WalletSdkMessages]?: SDKEventHandler<key> } = {}

  /** The "window" to post messages to */
  hostWindow?: Window

  businessId?: string
  accessToken?: string
  // TODO: change config to be typed
  config?: VatomConfig
  redirectedToBusiness = false

  /** Constructor */
  constructor(
    elem: HTMLDivElement | undefined = undefined,
    accessToken = '',
    businessId = '',
    config?: VatomConfig,
    refreshToken = ''
  ) {
    // Setup message listener
    window.addEventListener('message', this.onMessage)

    useVatomWalletStore.subscribe(state => {
      if (state.inventoryWasUpdated) {
        this.sendMsgWithoutResponse('walletsdk:inventoryWasUpdated')
      }
    })

    this.getDefaultConfig().then(defaultConfig => {
      // Store vars
      console.log('[Vatom VatomWallet] Mode: ' + (elem ? 'host container' : 'embedded'))
      this.businessId = businessId
      this.accessToken = accessToken
      this.config = config

      let iframe: HTMLIFrameElement | undefined = undefined
      if (elem) {
        this.handle('vatomwallet:pre-init', () => {
          this.sendMsgWithoutResponse('wallet-sdk-init', {
            accessToken,
            config,
            businessId,
            hostname: window.location.hostname,
            refreshToken
          })
          return true
        })
        iframe = document.createElement('iframe')
        iframe.setAttribute(
          'allow',
          'geolocation *; accelerometer *; gyroscope *; magnetometer *; ambient-light-sensor *; camera *; microphone *;'
        )
        // const script = iframe.contentDocument.document.createElement('script')
        // script.type = 'text/javascript'
        // script.src = `
        //   sessionStorage?.setItem('isEmbedded', 'true');
        //   sessionStorage?.setItem('VATOM_BUSINESS_ID', '${businessId}');
        //   sessionStorage?.setItem('VATOM_ACCESS_TOKEN', ${neededForAuth});
        //   sessionStorage?.setItem('VATOM_CONFIG', ${config});
        // `
        // iframe.contentDocument.document.body.appendChild(script)
        let src = `${config?.baseUrl ?? defaultConfig.baseUrl}`
        if (businessId) {
          src += `/b/${businessId}`
        }
        iframe.src = src
        iframe.style.height = '100%'
        iframe.style.width = '100%'
        iframe.style.border = 'none'
        elem.replaceChildren(iframe)
      }

      this.hostWindow = iframe?.contentWindow || window.opener || window.parent
      const isInIframe = window.parent && window.parent !== window
      const isInPopup = window.opener && window.opener !== window

      // console.log('isInIframe : ', isInIframe)
      // console.log('isInIPopup : ', isInPopup)
      // console.log('isEmbedded : ', isEmbedded)

      if (!VatomWallet.isEmbedded()) {
        sessionStorage?.setItem('isEmbedded', isInIframe.toString())
      }
      if (isInPopup) {
        try {
          const hostName = window.opener?.location?.hostname || window.opener?.location?.host
          sessionStorage?.setItem('popupHostName', hostName)
        } catch (e) {
          console.warn('Opened from cross-domain, could not find host name')
          sessionStorage?.setItem('popupHostName', '')
        }
      }
      if (!elem) {
        this.preInit()
      }

      this.handle('vatomwallet:SendUsersToWallet', async e => {
        // Get Users from vatom
        // localStorage.setItem('ui.vatom-users', JSON.stringify(e.data.payload.users))
        // localStorage.setItem('isMobile', e.data.payload.isMobile)
        console.log('send message across')
      })
    })
  }

  // Function to check if emebedded other than web
  preInit() {
    this.handle('wallet-sdk-init', this.handleInit)
    const target =
      (window as any).webkit?.messageHandlers?.vatomMessageHandler ||
      (window as any).vatomMessageHandler
    this.sendMsgWithoutResponse('vatomwallet:pre-init', undefined, target)
  }

  handleInit: SDKEventHandler<'wallet-sdk-init'> = e => {
    console.log('inititating init on client with data:', e)
    // // TODO: save access token, call login, etc.
    // const neededForAuth = {
    //   accessToken: e.accessToken,
    //   refreshToken: e.refreshToken,
    //   idToken: e.idToken
    // }
    sessionStorage?.setItem('isEmbedded', 'true')
    sessionStorage?.setItem('VATOM_BUSINESS_ID', e.businessId || '')
    sessionStorage?.setItem('VATOM_ACCESS_TOKEN', e.accessToken || '')
    sessionStorage?.setItem('VATOM_REFRESH_TOKEN', e.refreshToken || '')
    sessionStorage?.setItem('VATOM_CONFIG', JSON.stringify(e.config))
    sessionStorage?.setItem('POPUP_HOSTNAME', e.hostname || '')
    useVatomWalletStore.setState({ ...e.config })

    if (e.embeddedType) {
      sessionStorage?.setItem('embeddedType', e.embeddedType)
      if (e.embeddedType.includes('ios')) {
        navigator.geolocation.getCurrentPosition = this.getGeolocator(
          e.embeddedType
        ).getCurrentPosition
        navigator.geolocation.clearWatch = this.getGeolocator(e.embeddedType).clearWatch
        navigator.geolocation.watchPosition = this.getGeolocator(e.embeddedType).watchPosition
      }
    }

    VatomWallet._shared.sendMsgWithoutResponse('vatomwallet:loaded')
    window.dispatchEvent(new Event('vatomwallet:loaded'))

    return true
  }

  /** @private Send a raw payload to the other app */
  sendPayload(payload: any, target?: any) {
    // Send message to parent frame
    // TODO: Figure out if this is a security risk to use the "*" origin. It would allow anyone to embed the viewer inside
    // an iframe and then extract the user's refresh token after they login... Maybe this should instead be controlled
    // through a a list of whitelisted origins.
    try {
      console.log('[Vatom VatomWallet] Sending payload to other side: ', payload)
      console.log('[Vatom VatomWallet] getEmbeddedType: ', VatomWallet.getEmbeddedType())

      const embeddedType = VatomWallet.getEmbeddedType()
      let channel = target

      switch (embeddedType) {
        case 'flutter-ios':
        case 'flutter-android':
          channel = (window as any).vatomMessageHandler
          break
        case 'ios':
          channel = (window as any).webkit.messageHandlers.vatomMessageHandler
          break
      }

      console.log('[Vatom VatomWallet] channel: ', channel)

      if (channel) {
        return channel.postMessage(JSON.stringify(payload))
      }
      if (this.hostWindow && this.hostWindow != window)
        return this.hostWindow.postMessage(payload, '*')
      // Not in a host app
      // throw new Error("Not inside a host app.");
    } catch (error) {
      console.log('error on sendPayload: ', error)
    }
  }

  async getDefaultConfig() {
    return fetch('https://resources.vatom.com/system/defaults/walletsdk.json')
      .then(res => res.json())
      .then(result => result)
  }

  // Function overload that looks at the message name and payload to determine the return type while still allowing not passing a payload for those messages that don't need it
  async sendMsg<
    TMessage extends {
      [K in keyof WalletSdkMessages]: WalletSdkMessages[K]['payload'] extends undefined ? K : never
    }[keyof WalletSdkMessages]
  >(name: TMessage): Promise<WalletSdkMessages[TMessage]['response']>
  async sendMsg<TMessage extends keyof WalletSdkMessages>(
    name: TMessage,
    payload: WalletSdkMessages[TMessage]['payload'],
    target?: any,
    time?: number
  ): Promise<WalletSdkMessages[TMessage]['response']>

  /** Send a message to the parent app. @returns Promise */
  async sendMsg<TMessage extends keyof WalletSdkMessages>(
    name: TMessage,
    payload?: WalletSdkMessages[TMessage]['payload'],
    target?: any,
    time?: number
  ) {
    // Create unique ID for this request
    const id = crypto.randomUUID()

    // Send payload
    console.log(`[Vatom VatomWallet] Sending request to other side: id=${id}, name=${name}`)
    this.sendPayload({ id, name, payload, request: true }, target)

    // Create pending promise
    return new Promise<WalletSdkMessages[TMessage]['payload']>((resolve, reject) => {
      // Create timeout timer
      const timer = setTimeout(() => {
        // Fail it
        this.pending[id].reject(new Error(`Timed out waiting for response. ${name} ${id}`))
        delete this.pending[id]
      }, time || 15000)

      // Store pending promise
      this.pending[id] = { resolve, reject, timer }
    })
  }

  sendMsgWithoutResponse<
    TMessage extends {
      [K in keyof WalletSdkMessages]: WalletSdkMessages[K]['payload'] extends undefined ? K : never
    }[keyof WalletSdkMessages]
  >(name: TMessage): void
  sendMsgWithoutResponse<TMessage extends keyof WalletSdkMessages>(
    name: TMessage,
    payload: WalletSdkMessages[TMessage]['payload'],
    target?: any
  ): void

  /** Send a message and don't care about the response or even if it's received */
  sendMsgWithoutResponse<TMessage extends keyof WalletSdkMessages>(
    name: TMessage,
    payload?: WalletSdkMessages[TMessage]['payload'],
    target?: any
  ) {
    // Send it
    this.sendMsg(name, payload, target).catch(err => {
      // Ignore error
      // console.log('[Vatom VatomWallet] Response error ignored. ' + err.message)
    })
  }

  /** @private Called when a message is received */
  onMessage = <TMessage extends keyof WalletSdkMessages>(e: {
    data: {
      id: string
      name: TMessage
      request: boolean
      payload: WalletSdkMessages[TMessage]['payload']
      error?: string | undefined
    }
  }) => {
    // Extract params
    const { id, request, name, payload, error } = e.data

    // No ID given in message
    if (!id) {
      return
    }

    // Check if request or response
    if (request) {
      // Find handler and run it
      Promise.resolve()
        .then(e => {
          // Find handler
          const handler = this.handlers[name]
          if (!handler) throw new Error('No handler registered for ' + name)

          // Call it
          return handler(payload)
        })
        .then(payload => {
          // Send success response
          this.sendPayload({ id, payload, request: false })
        })
        .catch(err => {
          // Send failure response
          this.sendPayload({ id, error: err.message, request: false })
        })
    } else {
      // Response, find pending promise
      if (!this.pending[id]) {
        // Not found
        console.warn('[Vatom VatomWallet] No pending request for ID', id)
        return
      }

      // Resolve the pending promise
      if (error) this.pending[id].reject(new Error(error))
      else this.pending[id].resolve(payload)

      // Remove pending promise
      clearTimeout(this.pending[id].timer)
      delete this.pending[id]
    }
  }

  /** Register a handler function to handle a certain message */
  handle<TMessage extends keyof WalletSdkMessages, TCallBack extends SDKEventHandler<TMessage>>(
    name: TMessage,
    callback: TCallBack
  ) {
    this.handlers[name] = callback
  }

  onClose() {
    window.removeEventListener('message', this.onMessage)
    this.pending = {}
    this.handlers = {}
    delete this.hostWindow
  }

  getGeolocator(type: string) {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const clearWatch = (watchId: number) => {}
    const getCurrentPosition = async (
      successCallback: PositionCallback,
      errorCallback?: PositionErrorCallback | null,
      options?: PositionOptions
    ) => {
      try {
        const { coords } = (await this.sendMsg('vatomwallet:getCurrentPosition', undefined)) as any
        successCallback({ coords: coords, timestamp: new Date().getTime() })
      } catch (error: any) {
        if (errorCallback) {
          errorCallback({
            message: error.message,
            code: 2,
            PERMISSION_DENIED: 1,
            POSITION_UNAVAILABLE: 2,
            TIMEOUT: 3
          })
        }
      }
    }
    const watchPosition = (
      successCallback: PositionCallback,
      errorCallback?: PositionErrorCallback | null,
      options?: PositionOptions
    ) => {
      return 0
    }

    return {
      clearWatch,
      getCurrentPosition,
      watchPosition
    }
  }

  // Function to be called by the host to perform an action on a token
  async performAction(tokenId: string, actionName: string, payload?: any) {
    return JSON.parse(
      await this.sendMsg('walletsdk:performAction', {
        tokenId,
        actionName,
        actionPayload: payload
      })
    )
  }
  // Function to be called by the host to combine tokens
  async combineTokens(thisTokenId: string, otherTokenId: string) {
    return JSON.parse(await this.sendMsg('walletsdk:combineToken', { thisTokenId, otherTokenId }))
  }
  // Function to be called by the host to trash a token
  async trashToken(tokenId: string) {
    return JSON.parse(await this.sendMsg('walletsdk:trashToken', { tokenId }))
  }
  // Function to be called by the host to get a token
  async getToken(tokenId: string) {
    return JSON.parse(await this.sendMsg('walletsdk:getToken', { tokenId }))
  }
  // Function to be called by the host to get a public token
  async getPublicToken(tokenId: string) {
    return JSON.parse(await this.sendMsg('walletsdk:getPublicToken', { tokenId }))
  }
  // Function to be called by the host to get the list of tokens the user owns
  async listTokens() {
    return JSON.parse(await this.sendMsg('walletsdk:listTokens'))
  }

  async isLoggedIn() {
    return await this.sendMsgWithoutResponse('walletsdk:isLoggedIn')
  }

  async getPublicProfile(userId?: string) {
    return JSON.parse(await this.sendMsg('walletsdk:getPublicProfile', { userId }))
  }

  async getCurrentUser() {
    return JSON.parse(await this.sendMsg('walletsdk:getCurrentUser'))
  }

  async getBusinessTabs() {
    return JSON.parse(await this.sendMsg('walletsdk:getBusinessTabs'))
  }

  isNavigationReady() {
    const message = this.sendMsg('walletsdk:navigationIsReady', undefined, undefined, 250)
      .then(res => {
        return !!res
      })
      .catch(err => {
        console.log('isNavigationReady', err)
        return false
      })

    return message
  }

  // TODO: support params (for communities, etc.)
  async navigateToTab(tab: TabRoute, p?: any) {
    const params = { ...p, businessId: this.businessId }
    return await this.sendMsgWithoutResponse('walletsdk:navigateToTab', {
      route: tab,
      params
    })
  }

  async navigate(route: string, p?: any) {
    const params = { ...p, businessId: this.businessId }

    return await this.sendMsgWithoutResponse('walletsdk:navigate', { route: route, params: params })
  }
  async logOut() {
    await this.sendMsgWithoutResponse('walletsdk:logOut')
    return {}
  }
  async openCommunity(bussinesId: string, communityId?: string, roomId?: string | undefined) {
    return await this.sendMsgWithoutResponse('walletsdk:openCommunity', {
      bussinesId,
      communityId,
      roomId
    })
  }
}
interface PageThemeHeader {
  logo: string
}

interface PageThemeIconTitle {
  // Define properties if needed
}

interface PageThemeIcon {
  // Define properties if needed
}

interface PageThemeMain {
  // Define properties if needed
}

interface PageThemeEmptyState {
  // Define properties if needed
}

interface PageTheme {
  header: PageThemeHeader
  iconTitle: PageThemeIconTitle
  icon: PageThemeIcon
  main: PageThemeMain
  emptyState: PageThemeEmptyState
  mode: string
  pageTheme: string
}

interface PageText {
  emptyState: string
}

interface PageFeaturesNotifications {
  // Define properties if needed
}

interface PageFeaturesCard {
  // Define properties if needed
}

interface PageFeaturesFooterIcon {
  id: string
  src?: string
  title: string
}

interface PageFeaturesFooter {
  enabled: boolean
  icons: PageFeaturesFooterIcon[]
}

interface PageFeaturesVatom {
  // Define properties if needed
}

interface PageFeatures {
  notifications?: PageFeaturesNotifications
  card?: PageFeaturesCard
  footer?: PageFeaturesFooter
  vatom?: PageFeaturesVatom
  maxDistanceForPickup?: number
  businessHeader?: {
    hideHeader?: boolean
  }
  inventory?: {
    hideHeader?: boolean
  }
  icon?: {
    badges: boolean
    editions: boolean
    titles: boolean
  }
}

export interface PageConfig {
  theme?: PageTheme
  text?: PageText
  features?: PageFeatures
}
